import { Col, Dropdown, Input, MenuProps, Row, Spin } from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useMatch, useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import NavigationBar from '@components/navigation-bar';
import { TreeMenu } from '@src/emp-3.0/components/TreeMenu';
import { ITreeMenuItem } from '@src/emp-3.0/components/TreeMenu/types';
import DeployButton from '@src/pages/main/components/DeployButton';
import Highlighter from 'react-highlight-words';

import { useGetMetricList } from './api/queryHook';
import MetricsDetail from './components/MetricDetail';
import { METRICS_BY_FLOW, INDIVIDUAL_METRIC } from './constants';
import {
  LeftPanel,
  MetricListItemName,
  MetricListItemWrapper,
  PageContent,
  PageHeader,
  RightPanel,
  LeftPanelTitle,
  Wrapper,
} from './styled';
import StatusTag from '@src/emp-3.0/components/common/StatusTag';
import { useGetFlowList, useGetFlowOutputs } from '@src/emp-3.0/api/queryHooks';
import FlowDetail from 'pages/metric-templates/components/FlowDetail';
import { trackPageLoadTime } from '@src/sensor';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import { useMetricBuilderNavigate } from '@src/pages/metric-templates/hooks';
import EmptyState from './components/EmptyState';
import { useLocation } from 'react-router-dom';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { WorkbenchDeployButtons } from '../main/components/DeployButton/workbench';
import { useIsPatternMode } from './utils';

const defaultUserDefinedMetrics = [];
const defaultFlowList = [];
const defaultTreeMenuItemKey = '-1';
const itemKeyUrlMap = {};
const MetricTemplates: React.FC = () => {
  const { workbenchMode } = useInitialInfoStore();
  const path = `/metric-builder`;
  const navigate = useNavigate();
  const { navigateMetricBuilderPage, navigateFlowDetailPage, navigatePatternDetailPage } =
    useMetricBuilderNavigate();
  const { patternCreatePermission } = useIsPatternMode();
  const matchMetricDetailPage = useMatch<any, any>(`${path}/metric/:metricId`);
  const matchFlowPage = useMatch<any, any>(`${path}/flow/:flowId`);
  const matchPatternPage = useMatch<any, any>(`${path}/pattern/:flowId`);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>(defaultTreeMenuItemKey);
  const [searchText, setSearchText] = useState<string>('');
  const {
    data: userDefinedMetrics = defaultUserDefinedMetrics,
    isFetching: isGetMetricListLoading,
    refetch: refetchMetricList,
  } = useGetMetricList();
  const {
    data: flowList = defaultFlowList,
    isFetching: isGetFlowListLoading,
    refetch: refetchFlowList,
  } = useGetFlowList();

  const isDataReady =
    !isGetMetricListLoading &&
    !isGetFlowListLoading &&
    (userDefinedMetrics.length > 0 || flowList.length > 0);

  const location = useLocation();

  useEffect(() => {
    const isRootPath = location.pathname === path;

    if (!isRootPath || !isDataReady) return;

    if (userDefinedMetrics.length > 0) {
      navigate(`${path}/metric/${userDefinedMetrics[0].id}`);
      setSelectedKey(`metric_${userDefinedMetrics[0].id}`);
      setOpenKeys([INDIVIDUAL_METRIC]);
    } else if (flowList.length > 0) {
      navigate(`${path}/flow/${flowList[0].id}`);
      setSelectedKey(`flow_${flowList[0].id}`);
      setOpenKeys([METRICS_BY_FLOW]);
    }
  }, [location.pathname, path, isDataReady, userDefinedMetrics, flowList, navigate]);

  const { data: flowOutputsData = [], isLoading: isLoadingGetFlowOutputs } = useGetFlowOutputs();
  const isLoading = isGetMetricListLoading || isGetFlowListLoading || isLoadingGetFlowOutputs;

  const metricId = matchMetricDetailPage?.params.metricId;
  const flowId = matchFlowPage?.params.flowId;
  const patternId = matchPatternPage?.params.flowId;

  useEffect(() => {
    refetchMetricList().then();
  }, [metricId, refetchMetricList]);
  useEffect(() => {
    refetchFlowList().then();
  }, [flowId, patternId, refetchFlowList]);

  useEffect(() => {
    trackPageLoadTime('Semantic Mapper');
  }, []);

  // useEffect(() => {
  //   if (
  //     (matchMetricDetailPage && userDefinedMetrics.length === 0) ||
  //     (matchFlowPage && flowList.length === 0)
  //   ) {
  //     navigate(`${path}`, { replace: true });
  //   }
  // }, [
  //   matchMetricDetailPage,
  //   matchFlowPage,
  //   userDefinedMetrics.length,
  //   flowList.length,
  //   navigate,
  //   path,
  // ]);

  const scrollToMetric = () => {
    const MetricContainer = document.getElementById(`metric_${metricId}`);
    if (MetricContainer && MetricContainer.dataset.key === `metric_${metricId}`) {
      const divTop = MetricContainer.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;
      if (divTop > windowHeight || divTop < 0) {
        MetricContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };
  setTimeout(scrollToMetric, 500);

  const metricListItems = useMemo(() => {
    const res = userDefinedMetrics
      .filter(
        (metric) =>
          metric.name.toLowerCase().includes(searchText.toLowerCase()) &&
          !metric?.use_case_metric_id,
      )
      .map((metric) => {
        const lessThanDay = moment().isBefore(moment(metric.updated_at * 1000).add(1, 'd'));
        const itemKey = `metric_${metric.id}`;
        itemKeyUrlMap[itemKey] = `metric/${metric.id}`;
        return {
          itemKey: itemKey,
          label: (
            <MetricListItemWrapper data-key={`metric_${metric.id}`} id={`metric_${metric.id}`}>
              <MetricListItemName>
                <AddEllipsisWithTooltip text={metric.name} dataIndex="0">
                  <Highlighter
                    autoEscape
                    textToHighlight={metric.name}
                    highlightStyle={{
                      background: '#fff',
                      mixBlendMode: 'darken',
                      fontWeight: 500,
                    }}
                    searchWords={[searchText]}
                  />
                </AddEllipsisWithTooltip>
              </MetricListItemName>
              {metric.is_beta && (
                <StatusTag
                  type={'ghost'}
                  typeColor={'teal'}
                  style={{
                    height: '20px',
                    paddingInline: '7px',
                  }}
                >
                  Workbench
                </StatusTag>
              )}
              {metric.status === 0 && (
                <>
                  <StatusTag
                    type={lessThanDay ? 'primary' : 'ghost'}
                    typeColor={lessThanDay ? 'green' : 'orange'}
                    style={{
                      height: '20px',
                      paddingInline: '7px',
                    }}
                  >
                    {lessThanDay ? 'NEW' : 'Undeployed'}
                  </StatusTag>
                </>
              )}
            </MetricListItemWrapper>
          ),
        };
      });
    return res;
  }, [userDefinedMetrics, searchText]);

  const flowListItems = useMemo(() => {
    const searchRes = flowList.filter((flow) => {
      const allOutputs = flowOutputsData.map((output) => `${flow.name} ${output.name}`);
      return allOutputs.some((i) => i.toLowerCase().includes(searchText.toLowerCase()));
    });
    const res = searchRes.map((flow) => {
      const lessThanDay = moment().isBefore(moment(flow.updated_at * 1000).add(1, 'd'));
      const itemKey =
        flow.flow_definition_type === 'sql' ? `pattern_${flow.id}` : `flow_${flow.id}`;
      itemKeyUrlMap[itemKey] =
        flow.flow_definition_type === 'sql' ? `pattern/${flow.id}` : `flow/${flow.id}`;
      return {
        itemKey: itemKey,
        label: (
          <MetricListItemWrapper data-key={`metric_${flow.id}`} id={`metric_${flow.id}`}>
            <MetricListItemName>
              <AddEllipsisWithTooltip text={flow.name} dataIndex="0">
                <Highlighter
                  autoEscape
                  textToHighlight={flow.name}
                  highlightStyle={{
                    background: '#fff',
                    mixBlendMode: 'darken',
                    fontWeight: 500,
                  }}
                  searchWords={[...searchText.split(' ')]}
                />
              </AddEllipsisWithTooltip>
            </MetricListItemName>
            {flow.is_beta && (
              <StatusTag
                type={'ghost'}
                typeColor={'teal'}
                style={{
                  height: '20px',
                  paddingInline: '7px',
                }}
              >
                Workbench
              </StatusTag>
            )}
            {flow.status === 0 && (
              <>
                <StatusTag
                  type={lessThanDay ? 'primary' : 'ghost'}
                  typeColor={lessThanDay ? 'green' : 'orange'}
                  style={{
                    height: '20px',
                    paddingInline: '7px',
                  }}
                >
                  {lessThanDay ? 'NEW' : 'Undeployed'}
                </StatusTag>
              </>
            )}
          </MetricListItemWrapper>
        ),
      };
    });
    return res;
  }, [flowList, searchText, flowOutputsData]);

  const handleBuildMetricClick = (type: 'flow' | 'metric' | 'pattern') => {
    if (type === 'flow') {
      navigateFlowDetailPage('new');
    } else if (type === 'metric') {
      navigateMetricBuilderPage('new');
    } else if (type === 'pattern') {
      navigatePatternDetailPage('new');
    }
  };

  useEffect(() => {
    if (matchMetricDetailPage) {
      // handle refresh form /metric-builder/metric/:metricId page
      // there can be 2 codition :- either it is template defined or it is from userdefined
      const metricId = matchMetricDetailPage?.params.metricId;
      setOpenKeys([INDIVIDUAL_METRIC]);
      setSelectedKey(`metric_${metricId}`);
    } else if (matchFlowPage) {
      setOpenKeys([METRICS_BY_FLOW]);
      const flowId = matchFlowPage?.params.flowId;
      setSelectedKey(`flow_${flowId}`);
    } else if (matchPatternPage) {
      setOpenKeys([METRICS_BY_FLOW]);
      setSelectedKey(`pattern_${patternId}`);
    }
  }, [userDefinedMetrics, flowList, matchMetricDetailPage, matchPatternPage]);

  const menuItem: ITreeMenuItem[] = useMemo(() => {
    return [
      {
        label: METRICS_BY_FLOW,
        itemKey: METRICS_BY_FLOW,
        children: flowListItems,
      },
      {
        label: INDIVIDUAL_METRIC,
        itemKey: INDIVIDUAL_METRIC,
        children: metricListItems,
      },
    ];
  }, [flowListItems, metricListItems]);

  const handleMetricSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (value !== '') {
      setOpenKeys([INDIVIDUAL_METRIC, METRICS_BY_FLOW]);
    }
  };

  const handleMenuItemClick = (itemKey: string) => {
    let newOpenKeys = [...openKeys];
    if (openKeys.includes(itemKey)) {
      newOpenKeys = openKeys.filter((keys) => keys !== itemKey);
    } else {
      newOpenKeys.push(itemKey);
    }

    setSelectedKey(itemKey);
    setOpenKeys(newOpenKeys);

    if ([INDIVIDUAL_METRIC, METRICS_BY_FLOW].includes(itemKey)) {
      return;
    }

    if (itemKey === defaultTreeMenuItemKey) {
      navigate(`${path}`, { replace: true });
      return;
    }

    navigate(`${path}/${itemKeyUrlMap[itemKey]}`, { replace: true });
  };

  const renderCreateBtn = () => {
    const createItems: MenuProps['items'] = [
      {
        label: 'Create Flow',
        key: 'flow',
        onClick: () => handleBuildMetricClick('flow'),
      },
      {
        label: 'Create Individual Metric',
        key: 'metric',
        onClick: () => handleBuildMetricClick('metric'),
      },
    ];
    // allow pattern creation if user has access to pattern flow or workbench mode is enabled
    if (patternCreatePermission) {
      createItems.push({
        label: 'Create Pattern (Internal Only)',
        key: 'pattern',
        onClick: () => handleBuildMetricClick('pattern'),
      });
    }
    return (
      <Dropdown menu={{ items: createItems }} trigger={['click']}>
        <PermissionButton
          styleType="styled"
          style={{ height: '24px', borderRadius: '4px', fontWeight: 400 }}
        >
          + New Metric
        </PermissionButton>
      </Dropdown>
    );
  };

  return (
    <Wrapper>
      <NavigationBar />
      <PageHeader>
        <span style={{ fontWeight: '500' }}>Metric Builder</span>
        <div>{workbenchMode ? <WorkbenchDeployButtons /> : <DeployButton />}</div>
      </PageHeader>

      <PageContent>
        <LeftPanel>
          <Spin spinning={isLoading} style={{ height: '100%' }}>
            <Row>
              <Col span={15}>
                <LeftPanelTitle style={{ marginBottom: '20px' }}>Browse Metric</LeftPanelTitle>
              </Col>
              <Col span={9}>{renderCreateBtn()}</Col>
            </Row>
            <Input
              suffix={<SearchOutlined />}
              placeholder="Search a Metric..."
              allowClear
              onChange={handleMetricSearch}
              style={{ width: '100%', marginBottom: '8px' }}
            />
            <TreeMenu
              items={menuItem}
              openKeys={openKeys}
              selectedKey={selectedKey}
              onClick={handleMenuItemClick}
            />
          </Spin>
        </LeftPanel>
        <RightPanel>
          {/*resolve router type warning by degrade react type */}
          {/*https://github.com/facebook/react/issues/24304#issuecomment-1092563688*/}
          <Routes>
            <Route path={`/`} element={<EmptyState isLoading={isLoading} />} />
            <Route path={`/flow/:flowId`} element={<FlowDetail />} />
            <Route path={`/pattern/:flowId`} element={<FlowDetail />} />
            <Route path={`/metric/:metricId`} element={<MetricsDetail />} />
          </Routes>
        </RightPanel>
      </PageContent>
    </Wrapper>
  );
};

export default MetricTemplates;
